import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { listingsAPI, categoriesAPI, subscriptionsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Upload, Trash2, Star, Save, X, Camera, MapPin, CheckCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import MapLocationPicker from '../components/MapLocationPicker';
import DescriptionEditor from '../components/DescriptionEditor';

// Iter43 — Fixes 2, 4, 5, 6:
//  - Edit any field (incl. category/subcategory, location, lat/lng) for ANY status
//    incl. 'sold' (#2).
//  - Subcategory dropdown auto-populates from server (#5).
//  - Single-field location with MapLocationPicker + auto-permission detect (#3, #4).
//  - Publish / Mark Active buttons with sessionStorage-backed redirect to
//    /subscriptions when no active subscription is found (#6).
const DRAFT_STORAGE_KEY = (id) => `genzox_edit_draft_${id}`;

const EditListing = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(null);
  const [imageProgress, setImageProgress] = useState([]);
  const [formData, setFormData] = useState({
    title: '', description: '', price: '',
    location: '', pincode: '', city: '', state: '',
    lat: null, lng: null,
    category_id: '', subcategory_id: '',
  });
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Persist edits so that detouring through /subscriptions (Fix #6) doesn't lose data
  const persistDraft = useCallback((data) => {
    try { sessionStorage.setItem(DRAFT_STORAGE_KEY(id), JSON.stringify(data)); } catch (_) {}
  }, [id]);

  useEffect(() => {
    if (authLoading) return; // Don't redirect while AuthContext is still resolving
    if (!user) { navigate('/login'); return; }
    loadListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, id]);

  // Save formData to sessionStorage on every change so we can restore it after redirect
  useEffect(() => {
    if (!listing) return; // wait until initial load
    persistDraft(formData);
  }, [formData, listing, persistDraft]);

  const loadListing = async () => {
    try {
      const [listingRes, catRes] = await Promise.all([
        listingsAPI.getById(id),
        categoriesAPI.getAll(),
      ]);
      const data = listingRes.data;
      if (data.user_id !== user.user_id) {
        toast.error('You can only edit your own listings');
        navigate('/dashboard');
        return;
      }
      setListing(data);
      setCategories(catRes.data || []);
      const cat = (catRes.data || []).find((c) => c.category_id === data.category_id);
      setCategory(cat);

      // Fix #5: auto-populate category + subcategory from the saved listing.
      // Restore any local draft (from sessionStorage) so subscription-detour edits aren't lost (Fix #6).
      let restored = null;
      try {
        const raw = sessionStorage.getItem(DRAFT_STORAGE_KEY(id));
        if (raw) restored = JSON.parse(raw);
      } catch (_) { /* ignore corrupt drafts */ }
      setFormData((s) => ({
        ...s,
        title: restored?.title ?? data.title ?? '',
        description: restored?.description ?? data.description ?? '',
        price: restored?.price ?? data.price?.toString() ?? '',
        location: restored?.location ?? data.location ?? '',
        pincode: restored?.pincode ?? data.pincode ?? '',
        city: restored?.city ?? data.city ?? '',
        state: restored?.state ?? data.state ?? '',
        lat: restored?.lat ?? data.lat ?? null,
        lng: restored?.lng ?? data.lng ?? null,
        category_id: restored?.category_id ?? data.category_id ?? '',
        subcategory_id: restored?.subcategory_id ?? data.subcategory_id ?? '',
      }));
      // If we came back from a subscription purchase, show success toast + auto-publish/reactivate
      if (searchParams.get('resume') === '1') {
        const action = searchParams.get('action');
        toast.success('Plan activated. Resuming…');
        // Defer to next tick so state has settled
        setTimeout(() => {
          if (action === 'publish') handlePublish(true);
          else if (action === 'activate') handleMarkActive(true);
        }, 400);
      }
    } catch (e) {
      toast.error('Failed to load listing');
      navigate('/dashboard');
    } finally { setLoading(false); }
  };

  // Save edits (works for any status including 'sold' — Fix #2)
  const handleSave = async () => {
    if (!formData.title || !formData.description || !formData.price || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      await listingsAPI.update(id, {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        location: formData.location,
        pincode: formData.pincode,
        city: formData.city,
        state: formData.state,
        lat: formData.lat,
        lng: formData.lng,
        category_id: formData.category_id,
        subcategory_id: formData.subcategory_id,
      });
      try { sessionStorage.removeItem(DRAFT_STORAGE_KEY(id)); } catch (_) {}
      toast.success('Listing updated successfully!');
      navigate('/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update listing');
    } finally { setSaving(false); }
  };

  // Detour helper — redirect to /subscriptions with a return path back to this edit page
  const redirectToPlans = (action) => {
    const url = `/subscriptions?return=edit-listing&restore_listing=${encodeURIComponent(id)}&action=${action}`;
    navigate(url);
  };

  // Fix #6: Publish flow for draft listings
  const handlePublish = async (afterResume = false) => {
    // Save edits first
    if (!afterResume) {
      try {
        await listingsAPI.update(id, {
          title: formData.title,
          description: formData.description,
          price: parseFloat(formData.price),
          location: formData.location,
          pincode: formData.pincode,
          city: formData.city,
          state: formData.state,
          lat: formData.lat,
          lng: formData.lng,
          category_id: formData.category_id,
          subcategory_id: formData.subcategory_id,
        });
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to save edits before publishing');
        return;
      }
    }
    setPublishing(true);
    try {
      await listingsAPI.publish(id);
      try { sessionStorage.removeItem(DRAFT_STORAGE_KEY(id)); } catch (_) {}
      toast.success('Listing published!');
      navigate('/dashboard');
    } catch (e) {
      if (e?.response?.status === 402) {
        // No active plan → detour to plans page; we'll return here after purchase
        toast.warning('No active plan. Redirecting to choose one…');
        redirectToPlans('publish');
      } else {
        toast.error(e.response?.data?.detail || 'Publish failed');
      }
    } finally { setPublishing(false); }
  };

  // iter126 — Submit-for-Review: save latest edits → resubmit (rejected → draft)
  //           → publish (re-enters moderation). Mirrors handlePublish's 402 detour
  //           and outcome toasts, but is only exposed for rejected listings.
  const handleSubmitForReview = async () => {
    // Save latest edits first so the moderator sees the corrected copy.
    try {
      await listingsAPI.update(id, {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        location: formData.location,
        pincode: formData.pincode,
        city: formData.city,
        state: formData.state,
        lat: formData.lat,
        lng: formData.lng,
        category_id: formData.category_id,
        subcategory_id: formData.subcategory_id,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save edits before submitting');
      return;
    }
    setPublishing(true);
    try {
      // Step 1 — rejected → draft (backend transition)
      await listingsAPI.resubmit(id);
      // Step 2 — draft → moderation → pending_review / active / rejected
      const res = await listingsAPI.publish(id);
      try { sessionStorage.removeItem(DRAFT_STORAGE_KEY(id)); } catch (_) { /* ignore */ }
      const s = res?.data?.status;
      if (s === 'pending_review') {
        toast.success('Submitted for review. Our team will approve it shortly.');
      } else if (s === 'active') {
        toast.success('Approved and live again!');
      } else if (s === 'rejected') {
        const reasons = res?.data?.reasons || [];
        toast.error(reasons.length
          ? `Rejected again: ${reasons.join(' · ')}. Please make further edits and try again.`
          : 'Ad rejected again by moderation. Please review the listing and try again.');
        return; // stay on the edit page so the user can iterate
      } else {
        toast.success('Submitted for review.');
      }
      navigate('/dashboard');
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.warning('No active plan. Redirecting to choose one…');
        redirectToPlans('publish');
      } else {
        toast.error(e.response?.data?.detail || 'Failed to submit for review');
      }
    } finally { setPublishing(false); }
  };
  const handleMarkActive = async (afterResume = false) => {
    setPublishing(true);
    try {
      await listingsAPI.markActive(id);
      try { sessionStorage.removeItem(DRAFT_STORAGE_KEY(id)); } catch (_) {}
      toast.success('Listing is active again!');
      navigate('/dashboard');
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.warning('No active plan. Redirecting to choose one…');
        redirectToPlans('activate');
      } else {
        toast.error(e.response?.data?.detail || 'Failed to mark active');
      }
    } finally { setPublishing(false); }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const maxImages = category?.max_images || 12;
    const currentCount = listing?.images?.length || 0;
    if (currentCount + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed for this category`);
      e.target.value = '';
      return;
    }
    setUploading(true);
    setImageProgress(files.map((f) => ({ name: f.name, percent: 0 })));
    for (let i = 0; i < files.length; i++) {
      try {
        await listingsAPI.uploadImage(id, files[i], (evt) => {
          if (!evt.total) return;
          const pct = Math.min(100, Math.round((evt.loaded * 100) / evt.total));
          setImageProgress((prev) => {
            const next = [...prev];
            if (next[i]) next[i] = { ...next[i], percent: pct };
            return next;
          });
        });
      } catch (_) { toast.error(`Failed to upload ${files[i].name}`); }
    }
    try {
      const res = await listingsAPI.getById(id);
      setListing(res.data);
    } catch (_) { /* ignore */ }
    toast.success(files.length === 1 ? 'Image uploaded' : `${files.length} images uploaded`);
    setImageProgress([]);
    setUploading(false);
    e.target.value = '';
  };

  const handleDeleteImage = async (index) => {
    try {
      const res = await listingsAPI.deleteImage(id, index);
      setListing((prev) => ({ ...prev, images: res.data.images }));
      toast.success('Image deleted');
    } catch (e) { toast.error('Failed to delete image'); }
  };

  const handleSetCover = async (index) => {
    if (index === 0) return;
    try {
      const res = await listingsAPI.setCoverImage(id, index);
      setListing((prev) => ({ ...prev, images: res.data.images }));
      toast.success('Cover image updated');
    } catch (e) { toast.error('Failed to set cover image'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="animate-spin rounded-full h-14 w-14 border-t-4 border-[#1800ad]"></div>
      </div>
    );
  }
  if (!listing) return null;

  const maxImages = category?.max_images || 12;
  const images = listing.images || [];
  const selectedCat = categories.find((c) => c.category_id === formData.category_id) || category;
  const subcategories = selectedCat?.subcategories || [];
  const status = listing.status;
  const isDraft = status === 'draft' || status === 'pending_review';
  const isSold = status === 'sold';
  const isActive = status === 'active';
  // iter126 — Rejected ads need an explicit "Submit for Review" action so the
  // corrected ad can re-enter the moderation queue. Without it a rejected ad
  // is stuck forever.
  const isRejected = status === 'rejected';
  const rejectionReasons = Array.isArray(listing.moderation_reasons) ? listing.moderation_reasons : [];

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-8">
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center justify-between mb-6">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#6B7280] hover:text-[#1800ad] text-sm font-semibold transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft size={16} /> Back to Dashboard
          </Link>
          <button onClick={() => navigate('/dashboard')} className="p-2 rounded-lg hover:bg-gray-100 text-[#6B7280] hover:text-red-500 transition-colors" data-testid="cancel-edit">
            <X size={20} />
          </button>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <h1 className="text-3xl font-bold text-[#0A0B10]" style={{ fontFamily: 'Google Sans Flex' }} data-testid="edit-listing-title">Edit Listing</h1>
          <span
            data-testid={`status-badge-${status}`}
            className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${
              isActive ? 'bg-green-100 text-green-700' :
              isSold ? 'bg-orange-100 text-orange-700' :
              isDraft ? 'bg-blue-100 text-blue-700' :
              isRejected ? 'bg-red-100 text-red-700' :
              'bg-gray-100 text-gray-700'
            }`}
          >
            {status}
          </span>
        </div>

        {/* iter126 — Rejection context banner. Surfaces the moderator's / AI
             reasons so the user knows what to correct before resubmitting. */}
        {isRejected && (
          <div
            data-testid="rejection-banner"
            className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 sm:p-5"
          >
            <p className="text-[11px] font-bold uppercase tracking-wider text-red-600 mb-1">Ad rejected</p>
            <p className="text-sm text-[#4B5563] leading-relaxed">
              Please review the reasons below, make the required corrections, and hit <span className="font-semibold text-[#0A0B10]">Submit for Review</span> to send it back to moderation.
            </p>
            {rejectionReasons.length > 0 && (
              <ul className="mt-3 space-y-1 list-disc list-inside" data-testid="rejection-reasons">
                {rejectionReasons.map((r, i) => (
                  <li key={i} className="text-sm text-[#0A0B10]"><span className="font-medium">{r}</span></li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-3xl shadow-lg p-8 mb-6" data-testid="edit-form">
          <div className="space-y-5">
            {/* Category + Subcategory */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-[#0A0B10] mb-2">Category</label>
                <select
                  data-testid="edit-category"
                  value={formData.category_id}
                  onChange={(e) => {
                    const cid = e.target.value;
                    const newCat = categories.find((c) => c.category_id === cid);
                    setCategory(newCat);
                    setFormData((s) => ({ ...s, category_id: cid, subcategory_id: '' }));
                  }}
                  className="w-full rounded-2xl border border-gray-200 bg-gray-50/50 px-4 py-3.5 text-base text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none transition-all"
                >
                  {categories.map((c) => (
                    <option key={c.category_id} value={c.category_id}>{c.name}</option>
                  ))}
                </select>
              </div>
              {subcategories.length > 0 && (
                <div>
                  <label className="block text-sm font-bold text-[#0A0B10] mb-2">Subcategory</label>
                  <select
                    data-testid="edit-subcategory"
                    value={formData.subcategory_id}
                    onChange={(e) => setFormData((s) => ({ ...s, subcategory_id: e.target.value }))}
                    className="w-full rounded-2xl border border-gray-200 bg-gray-50/50 px-4 py-3.5 text-base text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none transition-all"
                  >
                    <option value="">— Select subcategory —</option>
                    {subcategories.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-[#0A0B10] mb-2">Title</label>
              <input data-testid="edit-title" type="text" value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full rounded-2xl border border-gray-200 bg-gray-50/50 px-4 py-3.5 text-base text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none transition-all" />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#0A0B10] mb-2">Description</label>
              <DescriptionEditor
                testId="edit-description"
                value={formData.description}
                onChange={(html) => setFormData({ ...formData, description: html })}
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#0A0B10] mb-2">Price (INR)</label>
              <input data-testid="edit-price" type="number" value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="w-full rounded-2xl border border-gray-200 bg-gray-50/50 px-4 py-3.5 text-base text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none transition-all" />
            </div>

            {/* Location — single field via MapLocationPicker (Fix #3 + #4). No manual PIN textarea. */}
            <div>
              <label className="block text-sm font-bold text-[#0A0B10] mb-2"><MapPin size={14} className="inline mr-1" />Location</label>
              <MapLocationPicker
                value={{
                  lat: formData.lat ?? null,
                  lng: formData.lng ?? null,
                  location: formData.location,
                  pincode: formData.pincode,
                  city: formData.city,
                  state: formData.state,
                }}
                onChange={(picked) => {
                  const isClear = picked.lat === null && picked.lng === null;
                  if (isClear) {
                    // Clear PIN: wipe ALL location fields atomically
                    setFormData((prev) => ({
                      ...prev,
                      lat: null,
                      lng: null,
                      location: '',
                      pincode: '',
                      city: '',
                      state: '',
                    }));
                    return;
                  }
                  setFormData((prev) => ({
                    ...prev,
                    lat: picked.lat ?? prev.lat,
                    lng: picked.lng ?? prev.lng,
                    location: picked.location || prev.location,
                    pincode: picked.pincode || prev.pincode,
                    city: picked.city || prev.city,
                    state: picked.state || prev.state,
                  }));
                }}
              />
            </div>
          </div>
        </div>

        {/* Image Management */}
        <div className="bg-white rounded-3xl shadow-lg p-8 mb-6" data-testid="image-management">
          <h2 className="text-xl font-bold text-[#0A0B10] mb-2" style={{ fontFamily: 'Google Sans Flex' }}>Images</h2>
          <p className="text-sm text-[#6B7280] mb-4">{images.length} of {maxImages} images. First image is the cover.</p>

          {images.length < maxImages && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <input type="file" accept="image/*" multiple onChange={handleImageUpload}
                className="hidden" id="edit-image-upload" disabled={uploading} data-testid="edit-image-upload" />
              <label htmlFor="edit-image-upload" className="cursor-pointer border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center hover:border-[#1800ad] hover:bg-[#f0edff] transition-colors">
                <Upload size={28} className="mx-auto mb-2 text-[#1800ad]" />
                <p className="text-sm font-bold text-[#0A0B10]">{uploading ? 'Uploading...' : 'Gallery / Files'}</p>
                <p className="text-[10px] text-[#9CA3AF]">Photos · Drive · Documents</p>
              </label>
              <input type="file" accept="image/*" capture="environment" onChange={handleImageUpload}
                className="hidden" id="edit-image-camera" disabled={uploading} data-testid="edit-image-camera" />
              <label htmlFor="edit-image-camera" className="cursor-pointer border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center hover:border-[#ff8500] hover:bg-[#fff4ed] transition-colors">
                <Camera size={28} className="mx-auto mb-2 text-[#ff8500]" />
                <p className="text-sm font-bold text-[#0A0B10]">Take Photo</p>
                <p className="text-[10px] text-[#9CA3AF]">Rear camera</p>
              </label>
            </div>
          )}

          {imageProgress.length > 0 && (
            <div className="bg-gray-50 rounded-xl p-3 mb-4 border border-gray-100 space-y-2" data-testid="edit-upload-progress">
              {imageProgress.map((p, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between text-[11px] text-[#0A0B10] mb-1">
                    <span className="truncate flex-1 mr-2">{p.name}</span>
                    <span className="font-bold text-[#1800ad]">{p.percent}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#1800ad] to-[#ff8500] transition-all duration-200" style={{ width: `${p.percent}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {images.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {images.map((img, index) => (
                <div key={`${img}-${index}`} className={`relative group rounded-xl overflow-hidden border-2 ${index === 0 ? 'border-[#ff8500]' : 'border-gray-200'}`}
                  data-testid={`edit-image-${index}`}>
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${img}`} alt={`Image ${index + 1}`}
                    className="w-full h-32 object-contain bg-gray-50" />
                  {index === 0 && (
                    <div className="absolute top-1.5 left-1.5 bg-[#ff8500] text-white text-[9px] px-2 py-0.5 rounded-full font-bold">
                      COVER
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    {index !== 0 && (
                      <button onClick={() => handleSetCover(index)} data-testid={`set-cover-${index}`}
                        className="p-2 bg-white rounded-lg text-[#ff8500] hover:bg-[#ff8500] hover:text-white transition-colors" title="Set as cover">
                        <Star size={16} />
                      </button>
                    )}
                    <button onClick={() => handleDeleteImage(index)} data-testid={`delete-image-${index}`}
                      className="p-2 bg-white rounded-lg text-red-600 hover:bg-red-600 hover:text-white transition-colors" title="Delete">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action buttons — status-aware (Fix #2 + #6) */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button data-testid="save-listing-btn" onClick={handleSave} disabled={saving || publishing}
            className="flex-1 bg-[#1800ad] text-white rounded-full py-4 font-bold text-lg hover:bg-[#1200a0] transition-colors shadow-lg shadow-[#1800ad]/20 disabled:opacity-50 flex items-center justify-center gap-2">
            <Save size={20} /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
          {isDraft && (
            <button data-testid="publish-listing-btn" onClick={() => handlePublish(false)} disabled={saving || publishing}
              className="flex-1 bg-[#ff8500] text-white rounded-full py-4 font-bold text-lg hover:bg-[#e57500] transition-colors shadow-lg shadow-[#ff8500]/20 disabled:opacity-50 flex items-center justify-center gap-2">
              <Send size={20} /> {publishing ? 'Publishing...' : 'Publish Ad'}
            </button>
          )}
          {isSold && (
            <button data-testid="mark-active-listing-btn" onClick={() => handleMarkActive(false)} disabled={saving || publishing}
              className="flex-1 bg-[#00bf63] text-white rounded-full py-4 font-bold text-lg hover:bg-[#00a554] transition-colors shadow-lg shadow-[#00bf63]/20 disabled:opacity-50 flex items-center justify-center gap-2">
              <CheckCircle size={20} /> {publishing ? 'Activating...' : 'Mark Active'}
            </button>
          )}
          {isRejected && (
            <button data-testid="submit-for-review-btn" onClick={handleSubmitForReview} disabled={saving || publishing}
              className="flex-1 bg-[#ff8500] text-white rounded-full py-4 font-bold text-lg hover:bg-[#e57500] transition-colors shadow-lg shadow-[#ff8500]/20 disabled:opacity-50 flex items-center justify-center gap-2">
              <Send size={20} /> {publishing ? 'Submitting...' : 'Submit for Review'}
            </button>
          )}
          <Link to="/dashboard" className="px-8 py-4 bg-gray-100 text-[#6B7280] rounded-full font-bold text-lg hover:bg-gray-200 transition-colors text-center" data-testid="cancel-save">
            Cancel
          </Link>
        </div>
      </div>
    </div>
  );
};

export default EditListing;
